import type { ReactNode } from "react";
import { IoAlertOutline, IoWarningOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import { css } from "../../../styled-system/css";
import { VStack } from "../../../styled-system/jsx";
import { ValidationList } from "./ValidationList";
import type { ValidationItem } from "./alertUtils";

const config = {
  error: {
    backgroundColor: "red/50",
    borderColor: "token(colors.red.300)",
    Icon: IoAlertOutline,
  },
  warning: {
    backgroundColor: "yellow/50",
    borderColor: "token(colors.yellow.400)",
    Icon: IoWarningOutline,
  },
} as const;

export function AlertSection({
  type,
  items,
  children,
}: {
  type: "error" | "warning";
  items: ValidationItem[];
  children?: ReactNode;
}) {
  const { t } = useTranslation(["uploads"]);
  const { backgroundColor, borderColor, Icon } = config[type];
  const count = items.length;

  const heading =
    type === "error" ?
      count > 0 ?
        t(($) => $.uploads.alerts["errors found — these must be corrected"], { count })
      : t(($) => $.uploads.alerts.Error)
    : count > 0 ? t(($) => $.uploads.alerts["potential issues found"], { count })
    : t(($) => $.uploads.alerts.Warning);

  return (
    <VStack
      alignItems="start"
      border={`1px solid ${borderColor}`}
      width="100%"
      paddingY="medium"
      paddingX="large"
      fontSize="medium"
      backgroundColor={backgroundColor}
    >
      <h5 className={css({ margin: 0 })}>
        <Icon className={css({ fontSize: "xxlarge" })} /> {heading}
      </h5>
      {items.length > 0 && <ValidationList items={items} />}
      {children}
    </VStack>
  );
}
